import React, { useEffect, useState, useCallback } from 'react';

const listeners = new Set();
let nextId = 1;

function emit(kind, message, opts = {}) {
  const id = nextId++;
  const toast = {
    id,
    kind,
    message: String(message ?? ''),
    durationMs: opts.durationMs ?? (kind === 'error' ? 6000 : 4000),
  };
  listeners.forEach(fn => fn({ type: 'add', toast }));
  if (toast.durationMs > 0) {
    setTimeout(() => {
      listeners.forEach(fn => fn({ type: 'remove', id }));
    }, toast.durationMs);
  }
  return id;
}

export const notify = {
  success: (msg, opts) => emit('success', msg, opts),
  error:   (msg, opts) => emit('error',   msg, opts),
  info:    (msg, opts) => emit('info',    msg, opts),
  warn:    (msg, opts) => emit('warn',    msg, opts),
};

const STYLES = {
  success: 'bg-emerald-600 text-white border-emerald-700',
  error:   'bg-red-600 text-white border-red-700',
  info:    'bg-sky-600 text-white border-sky-700',
  warn:    'bg-amber-500 text-white border-amber-600',
};
const ICONS = {
  success: '✓',
  error:   '✕',
  info:    'ℹ',
  warn:    '⚠',
};

export function Notifier() {
  const [toasts, setToasts] = useState([]);
  const handle = useCallback((evt) => {
    if (evt.type === 'add') {
      setToasts((curr) => [...curr, evt.toast]);
    } else if (evt.type === 'remove') {
      setToasts((curr) => curr.filter(t => t.id !== evt.id));
    }
  }, []);
  useEffect(() => {
    listeners.add(handle);
    return () => listeners.delete(handle);
  }, [handle]);

  if (toasts.length === 0) return null;
  return (
    <div
      className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] flex flex-col gap-2 w-[min(92vw,420px)] pointer-events-none"
      dir="rtl"
    >
      {toasts.map(t => (
        <div
          key={t.id}
          className={`pointer-events-auto rounded-lg shadow-lg border px-4 py-3 flex items-start gap-3 animate-in slide-in-from-top-2 ${STYLES[t.kind] || STYLES.info}`}
          role="status"
        >
          <span className="text-lg leading-none font-bold shrink-0 mt-0.5">{ICONS[t.kind] || ICONS.info}</span>
          <span className="text-sm leading-relaxed flex-1 break-words whitespace-pre-line">{t.message}</span>
          <button
            type="button"
            onClick={() => setToasts(curr => curr.filter(x => x.id !== t.id))}
            className="text-white/80 hover:text-white text-lg leading-none shrink-0"
            aria-label="إغلاق"
          >×</button>
        </div>
      ))}
    </div>
  );
}

/**
 * Replace the browser's native alert() so every legacy alert(msg) call
 * across the codebase renders as a toast instead. Returns the original
 * alert in case a future caller needs the real thing.
 *
 * Confirm() is NOT shimmed — it's synchronous and returns a boolean,
 * which a toast cannot replicate. Confirm callers should migrate to
 * an explicit confirm dialog when refactored.
 */
export function installAlertShim() {
  if (typeof window === 'undefined') return null;
  if (window.__notifyAlertShimInstalled) return null;
  const original = window.alert;
  window.alert = (msg) => {
    // Heuristic: messages containing "فشل" / "خطأ" / "error" → red,
    // "نجح" / "تم" → green, anything else → info.
    const s = String(msg ?? '');
    if (/فشل|خطأ|error|failed/i.test(s)) notify.error(s);
    else if (/نجح|تم\s|success/i.test(s)) notify.success(s);
    else notify.info(s);
  };
  window.__notifyAlertShimInstalled = true;
  return original;
}
